import cn from 'classnames'
import { useCallback, useState } from 'react'

import { Input } from 'core-components/library/inputs'
import { AbsolutePopup, PopPaper, TPopupVertical } from 'core-components/library/popup'
import { IPartnerContact } from 'partner360/features/contacts'

import { ContactSearchItem } from './ContactSearchItem'
import { useContactSearch } from './hooks/useContactSearch'
import { IContactSearchItem } from './models/IContactSearchItem'

import styles from './ContactSearch.module.scss'

interface IContactSearchProps {
  partnerId: string
  partnerContacts: IPartnerContact[]
  onSearch: (searchString: string) => void
  onAddToContacts: (contact: IContactSearchItem) => Promise<void>
}

export const ContactSearch = ({
  partnerId,
  partnerContacts,
  onSearch,
  onAddToContacts,
}: IContactSearchProps) => {
  const [isShowSuggestion, setIsShowSuggestion] = useState(false)
  const [allowToBlur, setAllowToBlur] = useState(true)
  const { filteredOptions, searchString, setSearchString } = useContactSearch({
    partnerId,
    partnerContacts,
  })

  const hideSuggestions = useCallback(() => {
    setIsShowSuggestion(false)
  }, [])

  const showSuggestions = useCallback(() => {
    setIsShowSuggestion(true)
  }, [])

  const handleFocusEvent = useCallback(() => {
    showSuggestions()
  }, [showSuggestions])

  const handleBlurEvent = useCallback(() => {
    if (allowToBlur) {
      hideSuggestions()
    }
  }, [allowToBlur, hideSuggestions])

  const handleSearch = useCallback(
    (value: string) => {
      setSearchString(value)
      onSearch(value)
    },
    [setSearchString, onSearch]
  )

  const handleClear = useCallback(() => {
    setSearchString('')
    onSearch('')
  }, [setSearchString, onSearch])

  const handleSelectContact = useCallback(
    (contact: IContactSearchItem) => {
      setSearchString(contact.fullName)
      setAllowToBlur(true)
      hideSuggestions()
      onSearch(contact.fullName)
    },
    [onSearch, setSearchString, hideSuggestions]
  )

  const handleAddToContacts = useCallback(
    async (contact: IContactSearchItem) => {
      hideSuggestions()
      await onAddToContacts(contact)
    },
    [onAddToContacts, hideSuggestions]
  )

  return (
    <div
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <AbsolutePopup
        visibilityCallback={() => {
          return isShowSuggestion && filteredOptions.length > 0
        }}
        verticalStartPosition={TPopupVertical.Bottom}
        popupContent={() => {
          return (
            <PopPaper className={styles.popup}>
              <div
                onMouseOut={() => setAllowToBlur(true)}
                onMouseOver={() => setAllowToBlur(false)}
              >
                {filteredOptions.map((option) => {
                  return (
                    <ContactSearchItem
                      key={option.contactId}
                      item={option}
                      onSelect={handleSelectContact}
                      onAddToContacts={handleAddToContacts}
                    />
                  )
                })}
              </div>
            </PopPaper>
          )
        }}
      >
        <Input
          onFocus={handleFocusEvent}
          onBlur={handleBlurEvent}
          classes={{
            root: styles.input,
          }}
          value={searchString}
          onChange={handleSearch}
          startContent={<i className="kbase kbase-search" />}
          endContent={
            searchString.length > 0 ? (
              <i className={cn(styles.clearIcon, 'kbase kbase-close')} onClick={handleClear} />
            ) : undefined
          }
          placeholder="Search for a contact"
        />
      </AbsolutePopup>
    </div>
  )
}
